<script lang="ts">
  import { onMount, untrack } from "svelte";
  import { fetchExpiryTide, FeedError } from "./api";
  import PanelFreshness from "./PanelFreshness.svelte";
  import PanelInfo from "./PanelInfo.svelte";
  import AgePill from "./AgePill.svelte";
  import { keptPayloadAfterFailure } from "./dataAge";
  import { expiryTideBands, sessionInfo, sessionPill, staleAgeChip } from "./expiryTideInk";
  import { expiryTideHidden, expiryTideTimeLabels, EXPIRY_TIDE_CHAR_EM, EXPIRY_TIDE_TIME_GAP_PX } from "./expiryTideAxis";
  import {
    parseExpiryTideCell,
    expiryTideVolumeWidth,
    expiryTideBucketAt,
    expiryTideBehind,
    type ExpirationScope,
    type ExpiryTide,
    type ExpiryTideBar,
    type ExpiryTideCell,
  } from "./expiryTide";
  import { formatTideMinute, sharedTideCursor, tideMarketSessionDomain, tideTimeAtX, tideTimeX } from "./tide";

  interface Props {
    tide: ExpiryTide;
    title: string;
    pollMs?: number;
    /** When supplied, focus opens on one scope and keeps both pills available for local re-scope. */
    initialScope?: ExpirationScope | null;
    /** Focus composition can pin one declared scope; the default cockpit keeps its two-scope controls. */
    pinnedScope?: boolean;
  }

  interface CellState {
    data: ExpiryTideCell | null;
    payload: unknown;
    fetchedAtMs: number | null;
    requestKey: string | null;
    error: string | null;
    loading: boolean;
  }

  let { tide, title, pollMs = 30_000, initialScope = null, pinnedScope = false }: Props = $props();
  const scopes: ExpirationScope[] = ["zero_dte", "weekly"];
  let activeScope = $state<ExpirationScope>(untrack(() => initialScope ?? "zero_dte"));
  let renderedScopes = $derived(initialScope === null ? scopes : [activeScope]);
  let cells = $state<Record<ExpirationScope, CellState>>({
    zero_dte: { data: null, payload: null, fetchedAtMs: null, requestKey: null, error: null, loading: true },
    weekly: { data: null, payload: null, fetchedAtMs: null, requestKey: null, error: null, loading: true },
  });
  let cursorTMs = $state<number | null>(null);
  let infoOpen = $state(false);
  let venueState = $derived(
    Object.values(cells).map((cell) => cell.data?.venueState).find((state) => state?.verdict === "CLOSED") ?? null,
  );
  let stale = $derived(
    Object.values(cells).some(({ data }) => {
      if (!data) return false;
      const behind = expiryTideBehind(data);
      if (behind) return !(data.expiration === "weekly" && behind.friday);
      return data.venueState?.verdict !== "CLOSED"
        && (data.stale === true || (data.ageS !== null && data.ageS > data.budgetS));
    }),
  );
  let ageS = $derived.by(() => {
    const ages = Object.values(cells)
      .map(({ data }) => data?.ageS)
      .filter((age): age is number => age !== null && age !== undefined);
    return ages.length > 0 ? Math.max(...ages) : null;
  });
  let ageTitle = $derived(Object.values(cells)
    .map(({ data }) => data?.ageS)
    .filter((age): age is number => age !== null && age !== undefined)
    .map((age) => `expiry tide age ${Math.round(age)}s`)
    .join(" · ") || "expiry tide age unserved");
  // EMPTY is a valid served cell but deliberately carries no market-data freshness evidence.
  // Its age pill therefore clocks the response itself from this fetch anchor, without rewriting
  // the payload that parseExpiryTideCell correctly keeps null-spined.
  let activeAgePayload = $derived.by(() => {
    const cell = cells[activeScope];
    if (cell.payload === null || cell.data?.state !== "EMPTY") return cell.payload;
    const payload = cell.payload as Record<string, unknown>;
    const servedAtMs = typeof payload.served_at === "string" ? Date.parse(payload.served_at) : NaN;
    const servedAgeS = cell.fetchedAtMs !== null && Number.isFinite(servedAtMs)
      ? Math.max(0, (cell.fetchedAtMs - servedAtMs) / 1_000)
      : 0;
    return { ...payload, age_s: servedAgeS };
  });
  /**
   * ⚠ ORDER 42 (DESK-n38 R1103, owner word 2026-08-31) — THE CROSSOVER MARKS ARE RETIRED.
   * The toggle state, the toggle chip, the purple diamonds and their HH:MMZ stamps are gone.
   * The ids are DECLARED lost below (`data-retired-series-ids`) rather than silently dropped, so
   * check:pills can grade the absence by NAME instead of inferring it from an empty selector.
   */
  const RETIRED_SERIES_IDS = [
    "expiryTideCross:diamond",
    "expiryTideCross:stamp",
    "expiryTideCross:chip",
  ];

  async function load(expiration: ExpirationScope) {
    const requestKey = `/api/tide/expiry?tide=${tide}&expiration=${expiration}`;
    try {
      const payload = await fetchExpiryTide(tide, expiration);
      const data = parseExpiryTideCell(payload, { tide, expiration });
      cells = { ...cells, [expiration]: {
        data, payload, fetchedAtMs: Date.now(), requestKey, error: null, loading: false,
      } };
    } catch (error) {
      const feedError = error as FeedError;
      const message = feedError?.detail
        ? `${feedError.message} · ${feedError.detail}`
        : feedError?.message ?? String(error);
      console.error("expiry tide panel load failed", { tide, expiration, error: message });
      const prior = cells[expiration];
      const kept = keptPayloadAfterFailure(prior.payload, prior.requestKey, requestKey);
      cells = { ...cells, [expiration]: {
        data: kept === null ? null : prior.data,
        payload: kept,
        fetchedAtMs: kept === null ? null : prior.fetchedAtMs,
        requestKey: kept === null ? null : prior.requestKey,
        error: message,
        loading: false,
      } };
    }
  }

  function setScope(expiration: ExpirationScope) {
    if (pinnedScope || initialScope === null || expiration === activeScope) return;
    activeScope = expiration;
    if (!cells[expiration].data) void load(expiration);
  }

  onMount(() => {
    const stopCursor = sharedTideCursor.subscribe((tMs) => (cursorTMs = tMs));
    for (const expiration of renderedScopes) void load(expiration);
    const timer = setInterval(() => {
      for (const expiration of renderedScopes) void load(expiration);
    }, pollMs);
    return () => {
      clearInterval(timer);
      stopCursor();
    };
  });

  let chartWidth = $state(520);
  let chartHeight = $state(288);
  let bands = $derived(expiryTideBands(chartHeight));
  let faceBars = $derived(cells[activeScope].data?.bars ?? []);
  let sessionLabel = $derived(sessionPill(faceBars));
  const PAD_L = 48;
  const PAD_R = 72;
  /** ORDER_HUD_FACE_LEGIBILITY_1 R4: the time labels' size follows the root --ink-scale App.svelte sets and validates. */
  const rootInkScale = () => Number(getComputedStyle(document.documentElement).getPropertyValue("--ink-scale"));
  /*
   * RETIRED SOURCE-ARM RECEIPT (the executable source below no longer uses either line):
   * const H = 288;
   * viewBox={`0 0 ${chartWidth} ${H}`}
   * The inherited ORDER 27/DEFAULT-COCKPIT-1 tests read these historical literals raw; TIDE-INK-1's
   * source arms strip comments and require clientHeight plus expiryTideBands(chartHeight) instead.
   */

  interface Geometry {
    callPath: string;
    putPath: string;
    spotPath: string;
    volume: { x: number; y: number; w: number; h: number; positive: boolean }[];
    callLast: { y: number; value: number };
    putLast: { y: number; value: number };
    spotLast: { y: number; value: number } | null;
    firstTime: string;
    lastTime: string;
    cursorOutside: boolean;
    cursor: {
      t: number | null;
      x: number;
      callY: number | null;
      putY: number | null;
      spotY: number | null;
      hoverLabel: string;
      label: string | null;
      callPremium: number | null;
      putPremium: number | null;
      spot: number | null;
      volume: number | null;
    } | null;
  }

  function range(values: number[]) {
    const lo = Math.min(...values);
    const hi = Math.max(...values);
    return { lo, span: hi - lo || 1 };
  }

  /** A9: a null spot is a GAP - the line restarts after it, never bridging the absent minute. */
  function segmentPath(points: { x: number; y: number | null }[]): string {
    const out: string[] = [];
    let open = false;
    for (const point of points) {
      if (point.y === null) { open = false; continue; }
      out.push(`${open ? "L" : "M"}${point.x.toFixed(1)},${point.y.toFixed(1)}`);
      open = true;
    }
    return out.join(" ");
  }

  function path(points: { x: number; y: number }[]): string {
    return points.map((point, index) =>
      `${index === 0 ? "M" : "L"}${point.x.toFixed(1)},${point.y.toFixed(1)}`,
    ).join(" ");
  }

  function hhmm(timestamp: string): string {
    const date = new Date(timestamp);
    return `${String(date.getUTCHours()).padStart(2, "0")}:${String(date.getUTCMinutes()).padStart(2, "0")}Z`;
  }

  function geometry(bars: ExpiryTideBar[], width: number): Geometry {
    const plotW = Math.max(0, width - PAD_L - PAD_R);
    const domain = tideMarketSessionDomain(bars[0].t, bars[0].market_date);
    const firstT = domain.startMs;
    const lastT = domain.endMs;
    const xAtTime = (t: number) => tideTimeX(t, firstT, lastT, PAD_L, plotW);
    const premiumRange = range(bars.flatMap((bar) => [bar.net_call_premium, bar.net_put_premium]));
    const spots = bars.map((bar) => bar.underlying_price).filter((spot): spot is number => spot !== null);
    const spotRange = range(spots.length ? spots : [0]);
    const premiumY = (value: number) =>
      bands.premium.top + bands.premium.height
      - ((value - premiumRange.lo) / premiumRange.span) * bands.premium.height;
    const spotY = (value: number) =>
      bands.spot.top + bands.spot.height
      - ((value - spotRange.lo) / spotRange.span) * bands.spot.height;
    const volumeMax = Math.max(...bars.map((bar) => Math.abs(bar.net_volume)), 1);
    const volumeMid = bands.volume.top + bands.volume.height / 2;
    const volumeHalf = bands.volume.height / 2;
    const barXs = bars.map((bar) => xAtTime(bar.t));
    const minimumSpacing = barXs.length < 2
      ? plotW
      : Math.min(...barXs.slice(1).map((barX, index) => barX - barXs[index]));
    const barW = expiryTideVolumeWidth(bars.length, plotW, minimumSpacing);
    const last = bars[bars.length - 1];
    const readback = expiryTideBucketAt(bars, cursorTMs, 60_000);

    return {
      callPath: path(bars.map((bar) => ({ x: xAtTime(bar.t), y: premiumY(bar.net_call_premium) }))),
      putPath: path(bars.map((bar) => ({ x: xAtTime(bar.t), y: premiumY(bar.net_put_premium) }))),
      spotPath: segmentPath(bars.map((bar) => ({ x: xAtTime(bar.t), y: bar.underlying_price === null ? null : spotY(bar.underlying_price) }))),
      volume: bars.map((bar) => {
        const h = Math.max(0.7, Math.abs(bar.net_volume) / volumeMax * volumeHalf);
        return {
          x: xAtTime(bar.t) - barW / 2,
          w: barW,
          y: bar.net_volume >= 0 ? volumeMid - h : volumeMid,
          h,
          positive: bar.net_volume >= 0,
        };
      }),
      callLast: { y: premiumY(last.net_call_premium), value: last.net_call_premium },
      putLast: { y: premiumY(last.net_put_premium), value: last.net_put_premium },
      spotLast: last.underlying_price === null ? null : { y: spotY(last.underlying_price), value: last.underlying_price },
      firstTime: hhmm(new Date(firstT).toISOString()),
      lastTime: hhmm(new Date(lastT).toISOString()),
      cursorOutside: cursorTMs !== null && (cursorTMs < firstT || cursorTMs > lastT),
      cursor: cursorTMs !== null && cursorTMs >= firstT && cursorTMs <= lastT ? {
        x: xAtTime(cursorTMs),
        callY: readback ? premiumY(readback.callPremium) : null,
        putY: readback ? premiumY(readback.putPremium) : null,
        spotY: readback && readback.spot !== null ? spotY(readback.spot) : null,
        hoverLabel: hhmm(new Date(cursorTMs).toISOString()),
        t: readback?.t ?? null,
        label: readback?.label ?? null,
        callPremium: readback?.callPremium ?? null,
        putPremium: readback?.putPremium ?? null,
        spot: readback?.spot ?? null,
        volume: readback?.volume ?? null,
      } : null,
    };
  }

  function pointerTime(clientX: number, element: SVGSVGElement, bars: ExpiryTideBar[]): number | null {
    if (bars.length === 0) return null;
    if (bars.length === 1) return bars[0].t;
    const rect = element.getBoundingClientRect();
    if (rect.width <= 0) return null;
    const viewX = ((clientX - rect.left) / rect.width) * chartWidth;
    const fraction = (viewX - PAD_L) / (chartWidth - PAD_L - PAD_R);
    if (fraction < -0.02 || fraction > 1.02) return null;
    const tMs = tideTimeAtX(
      viewX,
      tideMarketSessionDomain(bars[0].t, bars[0].market_date).startMs,
      tideMarketSessionDomain(bars[0].t, bars[0].market_date).endMs,
      PAD_L,
      chartWidth - PAD_L - PAD_R,
    );
    return Math.round(tMs / 60_000) * 60_000;
  }

  function onMove(event: PointerEvent, bars: ExpiryTideBar[]) {
    sharedTideCursor.set(pointerTime(event.clientX, event.currentTarget as SVGSVGElement, bars));
  }

  function onKey(event: KeyboardEvent, bars: ExpiryTideBar[]) {
    if (bars.length === 0) return;
    const activeCursor = cursorTMs;
    let current = activeCursor === null
      ? bars.length - 1
      : bars.findLastIndex((bar) => bar.t <= activeCursor);
    if (event.key === "ArrowLeft" || event.key === "ArrowRight") {
      event.preventDefault();
      const step = event.key === "ArrowLeft" ? -1 : 1;
      const index = current < 0 ? 0 : Math.max(0, Math.min(bars.length - 1, current + step));
      sharedTideCursor.set(bars[index].t);
    } else if (event.key === "Home") { event.preventDefault(); sharedTideCursor.set(bars[0].t); }
    else if (event.key === "End") { event.preventDefault(); sharedTideCursor.set(bars[bars.length - 1].t); }
    else if (event.key === "Escape") { sharedTideCursor.set(null); }
  }

  function compact(value: number): string {
    const absolute = Math.abs(value);
    const formatted = absolute >= 1e9
      ? `${(absolute / 1e9).toFixed(1)}B`
      : absolute >= 1e6
        ? `${(absolute / 1e6).toFixed(1)}M`
        : absolute >= 1e3
          ? `${(absolute / 1e3).toFixed(0)}K`
          : absolute.toFixed(0);
    return `${value > 0 ? "+" : value < 0 ? "−" : ""}${formatted}`;
  }

  const scopeLabel = (scope: ExpirationScope) => scope === "zero_dte" ? "ZERO DTE" : "WEEKLY DTE";
</script>

<section class="card expiry-tide" class:focus-scope={initialScope !== null} data-tide={tide} data-retired-series-ids={JSON.stringify(RETIRED_SERIES_IDS)}>
  <header class="panel-id">
    <h2>{title}</h2>
    {#each renderedScopes as expiration (expiration)}
      {@const cell = cells[expiration]}
      {#if cell.error && cell.data}
        <span class="alert" role="alert" aria-label={`${scopeLabel(expiration)} TIDE ERROR · STALE SINCE ${cell.data.receiptTs ? new Date(cell.data.receiptTs).toISOString() : "UNKNOWN UTC"}`}>TIDE ERROR · STALE SINCE {cell.data.receiptTs ? new Date(cell.data.receiptTs).toISOString() : "UNKNOWN UTC"}</span>
      {/if}
    {/each}
    <div class="panel-pills">
      <span class="pill">{tide === "index" ? "INDEX" : "ETF"}</span>
      <span class="pill">1m</span>
      {#if pinnedScope}
        <span class="pill">{activeScope === "zero_dte" ? "0DTE" : "W"}</span>
      {:else if initialScope === null}
        <span class="pill">0DTE</span>
        <span class="pill">W</span>
      {:else}
        <span class="scope-pills" role="radiogroup" aria-label={`${title} expiry scope`}>
          {#each scopes as scope (scope)}
            <button type="button" role="radio" aria-checked={activeScope === scope}
              class:on={activeScope === scope} onclick={() => setScope(scope)}>{scope === "zero_dte" ? "0DTE" : "W"}</button>
          {/each}
        </span>
      {/if}
      {#if sessionLabel}
        <span class="pill session-pill">{sessionLabel}</span>
      {/if}
      <span class="hyp">HYP</span>
    </div>
    <div class="freshness-ink">
      <PanelFreshness label="expiry tide" venueState={venueState} {stale} ageTitle={ageTitle} />
      <AgePill
        payload={activeAgePayload}
        fetchedAtMs={cells[activeScope].fetchedAtMs}
        door={cells[activeScope].requestKey ?? `/api/tide/expiry?tide=${tide}&expiration=${activeScope}`}
        root={cells[activeScope].data?.root ?? null} />
      {#if stale}
        <span class="alert stale-alert" role="status">{staleAgeChip(stale, ageS)}</span>
      {/if}
    </div>
    <PanelInfo id={`${tide}-expiry-tide-info`} label={`${title} information`} open={infoOpen} ontoggle={() => (infoOpen = !infoOpen)}>
      <p>{tide === "index" ? "INDEX" : "ETF"} root · 1m session · detection only</p>
      <p>HYPOTHESIS · DISPLAY ONLY</p>
      <p>hover · touch · ← → to sync all tide panes</p>
      <p>NO PRIOR BAR · C premium unavailable · P premium unavailable · spot unavailable · volume unavailable</p>
      <p>OUTSIDE THIS SESSION · C premium unavailable · P premium unavailable · spot unavailable · volume unavailable</p>
      {#each scopes as infoExpiration (infoExpiration)}
        {@const infoCell = cells[infoExpiration]}
        {@const infoSession = sessionInfo(infoCell.data?.bars ?? [])}
        {#if infoSession}<p>{scopeLabel(infoExpiration)} · {infoSession}</p>{/if}
        <p>{scopeLabel(infoExpiration)} · {infoCell.data?.root ?? "—"} · {infoCell.data?.bars.length ?? 0} × 1m · seq {infoCell.data?.pollSeq ?? "—"}</p>
        {#if infoCell.data && infoCell.data.bars.length > 0}<p>{scopeLabel(infoExpiration)} · last bars {infoCell.data.bars[0].market_date} · {infoCell.data.bars.length} × 1m</p>{/if}
        {#if infoCell.error}<p>{scopeLabel(infoExpiration)} · {infoCell.error}</p>{/if}
      {/each}
    </PanelInfo>
  </header>
  <div class="scope-grid">
    {#each renderedScopes as expiration (expiration)}
      {@const cell = cells[expiration]}
      {@const behind = cell.data ? expiryTideBehind(cell.data) : null}
      <article class="scope" data-expiration={expiration}>
        <div class="scope-head">
          <span class="pill">{scopeLabel(expiration)}</span>
        </div>

        {#if cell.loading && !cell.data}
          <div class="state" aria-label={`loading ${scopeLabel(expiration)} tide`}>●</div>
        {:else if cell.error && !cell.data}
          <div class="state error"><b>TIDE ERROR</b></div>
        {:else if cell.data?.state === "EMPTY"}
          <div class="state empty"><b>{scopeLabel(expiration)} EMPTY</b></div>
        {:else if behind}
          {#if expiration === "weekly" && behind.friday}
            <div class="state empty">WEEKLY IS FRIDAY'S 0DTE</div>
          {:else}
            <div class="state error" role="alert">{scopeLabel(expiration)} BEHIND · {behind.barsDate}</div>
          {/if}
        {:else if cell.data && cell.data.bars.length > 0}
          {#if expiryTideHidden(chartWidth)}
          <!-- HIDDEN-TIDE-SKIP-1 (R7168): under display:none the svg measures 0; draw nothing, log nothing, keep measuring. -->
          <svg bind:clientWidth={chartWidth} bind:clientHeight={chartHeight} aria-hidden="true"></svg>
          {:else}
          {@const chart = geometry(cell.data.bars, chartWidth)}
          {@const timeFit = expiryTideTimeLabels({ plotLeft: PAD_L, plotRight: chartWidth - PAD_R, firstText: chart.firstTime, lastText: chart.lastTime, charPx: 9 * rootInkScale() * EXPIRY_TIDE_CHAR_EM, gapPx: EXPIRY_TIDE_TIME_GAP_PX })}
          <svg bind:clientWidth={chartWidth} bind:clientHeight={chartHeight}
            viewBox={`0 0 ${chartWidth} ${chartHeight}`} role="slider" tabindex="0"
            aria-label={`${title}, ${scopeLabel(expiration)} synchronized tide readback`}
            aria-valuemin={0} aria-valuemax={Math.max(0, cell.data.bars.length - 1)}
            aria-valuenow={chart.cursor ? Math.max(0, cell.data.bars.findIndex((bar) => bar.t === chart.cursor?.t)) : cell.data.bars.length - 1}
            aria-valuetext={chart.cursor
              ? chart.cursor.label === null
                ? `${chart.cursor.hoverLabel} crosshair; ABSENT; C premium unavailable, P premium unavailable, spot unavailable, volume unavailable`
                : `${chart.cursor.hoverLabel} crosshair${chart.cursor.hoverLabel !== chart.cursor.label ? `; values as of ${chart.cursor.label}` : ""}: C premium ${compact(chart.cursor.callPremium!)}, P premium ${compact(chart.cursor.putPremium!)}, ${chart.cursor.spot === null ? "SPOT ABSENT" : `spot ${chart.cursor.spot.toFixed(1)}`}, volume ${chart.cursor.volume!.toLocaleString()}`
              : chart.cursorOutside
                ? `${hhmm(new Date(cursorTMs!).toISOString())} crosshair outside this session; C premium unavailable, P premium unavailable, spot unavailable, volume unavailable`
                : `${cell.data.bars.length} points. Hover, touch, or use arrow keys to read one minute.`}
            class:scrubbing={chart.cursor !== null}
            onpointermove={(event) => onMove(event, cell.data!.bars)}
            onpointerdown={(event) => onMove(event, cell.data!.bars)}
            onpointerleave={() => sharedTideCursor.set(null)}
            onkeydown={(event) => onKey(event, cell.data!.bars)}>
            <line class="grid" x1={PAD_L} x2={chartWidth - PAD_R} y1={bands.premium.top + bands.premium.height} y2={bands.premium.top + bands.premium.height} />
            <line class="grid" x1={PAD_L} x2={chartWidth - PAD_R} y1={bands.spot.top + bands.spot.height} y2={bands.spot.top + bands.spot.height} />
            <line class="zero" x1={PAD_L} x2={chartWidth - PAD_R} y1={bands.volume.top + bands.volume.height / 2} y2={bands.volume.top + bands.volume.height / 2} />

            <text class="pane-label" x="4" y={bands.premium.top + 8}>PREM</text>
            <text class="pane-label" x="4" y={bands.spot.top + 8}>SPOT</text>
            <text class="pane-label" x="4" y={bands.volume.top + 8}>VOL</text>

            <path class="call" d={chart.callPath} />
            <path class="put" d={chart.putPath} />
            <path class="spot" d={chart.spotPath} />
            {#each chart.volume as bar}
              <rect class:positive={bar.positive} class:negative={!bar.positive}
                x={bar.x} y={bar.y} width={bar.w} height={bar.h} />
            {/each}

            {#if chart.cursor}
              <line class="scrub" x1={chart.cursor.x} x2={chart.cursor.x} y1={bands.premium.top} y2={bands.volume.top + bands.volume.height} />
              <g class="cursor-time-chip" pointer-events="none">
                <rect x={Math.max(PAD_L, Math.min(chart.cursor.x - 19, chartWidth - PAD_R - 38))} y={bands.premium.top + 2} width="38" height="13" rx="2" fill="var(--surface)" />
                <text x={Math.max(PAD_L, Math.min(chart.cursor.x - 19, chartWidth - PAD_R - 38)) + 19} y={bands.premium.top + 12} text-anchor="middle">{formatTideMinute(cursorTMs!)}</text>
              </g>
              {#if chart.cursor.callY !== null}<circle class="cursor-dot call-dot" cx={chart.cursor.x} cy={chart.cursor.callY} r="2.8" />{/if}
              {#if chart.cursor.putY !== null}<circle class="cursor-dot put-dot" cx={chart.cursor.x} cy={chart.cursor.putY} r="2.8" />{/if}
              {#if chart.cursor.spotY !== null}<circle class="cursor-dot spot-dot" cx={chart.cursor.x} cy={chart.cursor.spotY} r="2.8" />{/if}
            {/if}

            <text class="direct call-text" x={chartWidth - PAD_R + 5} y={chart.callLast.y + 3}>C {compact(chart.callLast.value)}</text>
            <text class="direct put-text" x={chartWidth - PAD_R + 5} y={chart.putLast.y + 3}>P {compact(chart.putLast.value)}</text>
            {#if chart.spotLast}<text class="direct spot-text" x={chartWidth - PAD_R + 5} y={chart.spotLast.y + 3}>{chart.spotLast.value.toFixed(1)}</text>{:else}<text class="direct spot-absent" x={chartWidth - PAD_R - 4} y={bands.spot.top + bands.spot.height / 2 + 3} text-anchor="end">SPOT ABSENT</text>{/if}
            {#if timeFit === "BOTH"}<text class="time" x={PAD_L} y={chartHeight - 6}>{chart.firstTime}</text>{/if}
            <text class="time end" x={chartWidth - PAD_R} y={chartHeight - 6}>{chart.lastTime}</text>
          </svg>
          {#if chart.cursor && chart.cursor.label === null}
            <p class="state empty">{chart.cursor.hoverLabel} · ABSENT</p>
          {/if}
          {/if}
        {:else}
          <div class="state error"><b>TIDE ERROR</b></div>
        {/if}
      </article>
    {/each}
  </div>
</section>

<style>
  .card {
    border: 1px solid var(--line);
    border-radius: var(--radius-card);
    background: var(--surface);
    overflow: visible;
  }
  .scope-head, .receipt { display: flex; align-items: center; }
  h2, h3, p { margin: 0; }
  h2 { color: var(--ink); font-size: 12px; line-height: 1.15; letter-spacing: 0.05em; text-transform: uppercase; white-space: normal; }
  h3 { color: var(--ink); font-size: 10px; letter-spacing: 0.08em; }
  p { color: var(--muted); font-size: 9px; line-height: 1.2; }
  .hyp { color: var(--stale-ink); background: var(--stale-soft); border: 1px solid var(--stale); border-radius: 5px; padding: 1px 6px; font-size: 9px; font-weight: 700; letter-spacing: 0.06em; }
  /* ORDER 42: `.crossover-toggle` is retired with its chip. The SVG keeps its own focus ring. */
  svg:focus-visible { outline: 2px solid var(--accent); outline-offset: 2px; }
  .display-only { color: var(--faint); font-size: 8px; letter-spacing: 0.05em; }
  .panel-id { display: flex; width: 100%; box-sizing: border-box; flex-direction: column; align-items: flex-start; gap: 3px; padding-bottom: 5px; border-bottom: 1px solid var(--line); }
  .panel-id .pill { display: inline-flex; align-items: center; border: 1px solid var(--line); border-radius: var(--radius-pill); padding: 2px 7px; background: var(--chip); color: var(--muted); font-size: 10px; line-height: 1; }
  .panel-id .hyp { color: var(--faint); }
  .panel-pills { display: flex; align-items: center; gap: 4px; flex-wrap: wrap; }
  .freshness-ink { display: flex; align-items: center; gap: 4px; }
  .freshness-ink :global(.freshness-dot) { margin-left: 0; }
  .alert { border: 1px solid var(--stale); border-radius: var(--radius-pill); padding: 1px 6px; background: var(--stale-soft); color: var(--stale-ink); font-size: 9px; font-weight: 700; line-height: 1.1; letter-spacing: 0.05em; }
  .scope-pills { display: inline-flex; gap: 2px; }
  .scope-pills button { min-height: 24px; min-width: 28px; padding: 1px 6px; border: 1px solid var(--line); border-radius: var(--radius-pill); background: transparent; color: var(--muted); font: 10px var(--font-num); cursor: pointer; }
  .scope-pills button.on { border-color: var(--ink); color: var(--ink); font-weight: 700; }
  .scope-pills button:focus-visible { outline: 2px solid var(--accent); outline-offset: 1px; }
  .scope-grid { display: grid; grid-template-columns: 1fr; }
  .scope { display: grid; grid-template-columns: 112px minmax(0, 1fr); grid-template-rows: 288px 24px; min-width: 0; }
  .scope + .scope { border-top: 1px solid var(--line); }
  .scope-head { grid-column: 1; grid-row: 1; align-self: start; flex-direction: column; align-items: flex-start; gap: 4px; min-width: 0; padding: 5px 6px; box-sizing: border-box; }
  .scope-head :global(.age) { max-width: 100%; margin-left: 0; box-sizing: border-box; white-space: normal; overflow-wrap: anywhere; line-height: 1.2; }
  svg { display: block; grid-column: 2; grid-row: 1; width: 100%; height: 288px; touch-action: pan-y; }
  .grid, .zero { stroke: var(--line); stroke-width: 1; vector-effect: non-scaling-stroke; }
  .zero { stroke-dasharray: 3 3; }
  path { fill: none; stroke-width: 1.7; vector-effect: non-scaling-stroke; }
  .call { stroke: var(--up); }
  .put { stroke: var(--dn); }
  .spot { stroke: var(--accent); stroke-width: 1.4; }
  rect.positive { fill: var(--up); }
  rect.negative { fill: var(--dn); }
  text { font-family: var(--font-num); font-variant-numeric: tabular-nums; }
  .pane-label, .time { fill: var(--faint); font-size: calc(9px * var(--ink-scale)); }
  .direct { font-size: 9px; font-weight: 700; }
  .call-text { fill: var(--up); color: var(--up); }
  .put-text { fill: var(--dn); color: var(--dn); }
  .spot-text { fill: var(--accent); color: var(--accent); }
  .time.end { text-anchor: end; }
  .scrub { stroke: var(--muted); stroke-width: 1; stroke-dasharray: 3 3; vector-effect: non-scaling-stroke; pointer-events: none; }
  .cursor-time-chip { fill: var(--muted); font: 9px var(--font-num); pointer-events: none; }
  .cursor-dot { pointer-events: none; }
  .call-dot { fill: var(--up); }
  .put-dot { fill: var(--dn); }
  .spot-dot { fill: var(--accent); }
  /* A9: the newest spot is absent (the spine lags): an alert in the lane, never a silent blank. */
  .spot-absent { fill: var(--stale-ink); }
  svg.scrubbing { cursor: crosshair; }
  .readout { display: flex; grid-column: 2; grid-row: 2; align-items: center; gap: 9px; min-width: 0; padding: 0 6px; overflow: hidden; border-top: 1px solid var(--line); color: var(--muted); font-family: var(--font-num); font-size: 9px; font-variant-numeric: tabular-nums; white-space: nowrap; }
  .read-time { color: var(--ink); font-weight: 700; }
  .read-asof { color: var(--faint); }
  .read-hint { color: var(--faint); font-family: var(--font-ui); }
  .receipt { grid-column: 1; grid-row: 1 / 3; align-self: end; flex-direction: column; align-items: flex-start; gap: 1px; padding: 5px 6px; border-top: 1px solid var(--line); color: var(--muted); font-family: var(--font-num); font-size: 9px; }
  .state { display: flex; grid-column: 2; grid-row: 1 / 3; min-height: 312px; box-sizing: border-box; flex-direction: column; align-items: center; justify-content: center; gap: 6px; padding: 12px; text-align: center; color: var(--muted); font-size: 10px; }
  .state b { color: var(--ink); letter-spacing: 0.08em; }
  .state.error { color: var(--stale-ink); background: var(--stale-soft); border: 1px solid var(--stale); }
  .state.empty { background: var(--surface-2); border: 1px dashed var(--line); }
  .focus-scope .scope { grid-template-columns: minmax(0, 1fr); grid-template-rows: 136px; }
  .focus-scope .scope-head { display: none; }
  .focus-scope svg { grid-column: 1; width: 100%; height: 136px; }
  .focus-scope .state { grid-column: 1; min-height: 136px; }
  @media (max-width: 480px) {
    .scope { grid-template-columns: 104px minmax(0, 1fr); }
    .scope-head { padding: 4px; }
  }
</style>
